import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, ArrowUpDown, ChevronUp, ChevronDown, MapPin } from 'lucide-react'
import { Badge } from 'components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { PortfolioCircleAvatar } from 'components/PortfolioCircleAvatar'
import { TablePagination } from 'components/ui/pagination'
import PortfolioActionSheet from './PortfolioActionSheet'
import useIsMobile from 'hooks/useIsMobile'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'

/**
 * Builds the avatar's image list from a portfolio row.
 *
 * Rows reaching this table are the narrow projection built in `Portfolios.js`,
 * which drops `locations` and precomputes `locationImages` (a flat string[]) —
 * the same field the Cards view consumes via `PortfolioCard`. Reading
 * `locations` here therefore always produced an empty array, so every row fell
 * back to the portfolio initial instead of showing its location mosaic.
 *
 * The populated `locations` shape is kept as a fallback so this still works if
 * a raw, unprojected portfolio is ever passed in.
 */
const avatarLocations = portfolio => {
	if (portfolio.locationImages?.length) {
		return portfolio.locationImages.map(url => ({
			imageUrl: url,
			name: portfolio.assetName || '',
		}))
	}
	return (portfolio.locations || [])
		.map(entry => entry?.location)
		.filter(Boolean)
		.map(loc => ({ imageUrl: loc?.image, name: loc?.assetName || '' }))
}

const sortFields = [
	{ id: 'assetName', label: 'Portfolio' },
	{ id: 'description', label: 'Description' },
	{ id: 'portfolioType', label: 'Type' },
	{ id: 'locationCount', label: 'Locations' },
	{ id: 'updatedAt', label: 'Date Updated' },
]

const PortfoliosTable = ({ portfolios, recordCount, userId }) => {
	const isMobile = useIsMobile()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	// Sheet state
	const [sheetOpen, setSheetOpen] = useState(false)
	const [selectedPortfolio, setSelectedPortfolio] = useState(null)

	// The owner avatar opens the shared partner profile popup (MYD-535), as on
	// the Locations and Projects lists.
	const { openPartner, partnerDialog } = usePartnerProfileDialog()

	const handleThreeDotClick = (event, portfolio) => {
		event.stopPropagation()
		setSelectedPortfolio(portfolio)
		setSheetOpen(true)
	}

	const handleAvatarClick = (event, record) => {
		event.stopPropagation()
		openPartner(record?.owner?._id, record?.owner)
	}

	const handleSort = fieldId => {
		if (orderBy === fieldId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(fieldId)
			setOrder('asc')
		}
		setPage(0)
	}

	const sortedPortfolios = useMemo(() => {
		if (!orderBy) return portfolios

		return [...portfolios].sort((a, b) => {
			if (orderBy === 'updatedAt') {
				const aVal = new Date(a.updatedAt).getTime() || 0
				const bVal = new Date(b.updatedAt).getTime() || 0
				if (aVal < bVal) return order === 'asc' ? -1 : 1
				if (aVal > bVal) return order === 'asc' ? 1 : -1
				return 0
			}
			const aVal = (a[orderBy] ?? '').toString().toLowerCase()
			const bVal = (b[orderBy] ?? '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [portfolios, orderBy, order])

	const paginatedPortfolios = useMemo(() => {
		// On a phone the list is unpaginated and scrolled, so every row renders.
		// Hiding the pager without this would strand every record past the tenth.
		if (isMobile) return sortedPortfolios
		const start = page * rowsPerPage
		return sortedPortfolios.slice(start, start + rowsPerPage)
	}, [sortedPortfolios, page, rowsPerPage, isMobile])

	const SortIcon = ({ fieldId }) => {
		if (orderBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const formatDate = dateStr => {
		if (!dateStr) return ''
		return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
	}

	return (
		<>
			<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white p-4">
				{/* Sort pills. Hidden on mobile: five pills wrap onto two lines at
				    390px, costing most of the first screenful before a single record
				    appears. Mobile only, deliberately — this list renders flex rows
				    rather than a real table, so there is no column header behind
				    these and hiding them everywhere would strip sorting from the
				    desktop List view entirely. The sort state itself is untouched. */}
				{!isMobile && (
				<div className="mb-3 flex items-center gap-1.5 flex-wrap">
					<span className="text-xs text-gray-400 mr-1">Sort by:</span>
					{sortFields.map(field => (
						<button
							key={field.id}
							type="button"
							onClick={() => handleSort(field.id)}
							className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
								orderBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
							}`}
						>
							{field.label}
							<SortIcon fieldId={field.id} />
						</button>
					))}
				</div>
				)}

				{/* List items */}
				<div className="divide-y divide-gray-100">
					{paginatedPortfolios.map(portfolio => (
						<div key={portfolio._id} className="flex items-center gap-3 py-2 group">
							{/* Avatar — composite of up to 2 location images, falls back to portfolio initial */}
							<PortfolioCircleAvatar
								locations={avatarLocations(portfolio)}
								fallbackInitial={portfolio.assetName?.[0] || 'P'}
								size="sm"
								className="flex-shrink-0"
							/>

							{/* Portfolio name */}
							<Link
								to={`/portfolios/${portfolio._id}`}
								className="text-sm font-medium text-primary hover:underline truncate min-w-0"
							>
								{portfolio.assetName}
							</Link>

							{/* Description */}
							<span className="text-sm text-gray-500 truncate min-w-0">{portfolio.description}</span>

							{/* Portfolio type badge */}
							{portfolio.portfolioType && (
								<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
									{portfolio.portfolioType}
								</Badge>
							)}

							{/* Location count */}
							{portfolio.locationCount > 0 && (
								<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5 flex-shrink-0">
									<MapPin className="w-2.5 h-2.5" />
									{portfolio.locationCount}
								</Badge>
							)}

							{/* Updated date */}
							{portfolio.updatedAt && (
								<span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
									{formatDate(portfolio.updatedAt)}
								</span>
							)}

							{/* Edit — right-justified */}
							<div className="ml-auto flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity">
								{portfolio.owner?._id === userId ? (
									<button
										type="button"
										className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
										onClick={e => handleThreeDotClick(e, portfolio)}
									>
										<ChevronRight className="h-4 w-4" />
									</button>
								) : (
									<Avatar className="h-7 w-7 cursor-pointer" onClick={e => handleAvatarClick(e, portfolio)}>
										<AvatarImage src={portfolio.owner?.image} />
										<AvatarFallback />
									</Avatar>
								)}
							</div>
						</div>
					))}
				</div>

				{!isMobile && recordCount > 10 && (
					<TablePagination
						page={page}
						rowsPerPage={rowsPerPage}
						count={recordCount}
						onPageChange={setPage}
						onRowsPerPageChange={setRowsPerPage}
					/>
				)}
			</div>

			{partnerDialog}

			<PortfolioActionSheet
				portfolio={selectedPortfolio}
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				isOwner={selectedPortfolio?.owner?._id === userId}
			/>
		</>
	)
}

export default PortfoliosTable
